import type { GetFormCompletionTimeTrendsResponse } from '#services/api/insights.ts';

import { Box, Skeleton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ChartDataProvider,
  ChartsAxisHighlight,
  ChartsLegend,
  ChartsSurface,
  ChartsTooltip,
  ChartsXAxis,
  ChartsYAxis,
  LinePlot,
  MarkPlot,
} from '@mui/x-charts-pro';
import { useQuery } from '@tanstack/react-query';

import { formCompletionTimeTrends } from '#lib/react-query/queries.ts';
import { Base } from '#routes/Home/Widgets/Base.tsx';
import {
  InfoEmphasis,
  InfoList,
  InfoParagraph,
  InfoSection,
  InfoTemplate,
} from '#routes/Home/Widgets/InfoTemplateComponents.tsx';
import {
  CHART_AXIS_STYLES,
  CHART_SERIES_COLORS,
  CHART_X_AXIS_CONFIG,
  WIDGET_CHART_HEIGHT,
} from '#utils/charts.ts';
import { DAY_IN_SECONDS, formatDurationFromSeconds } from '#utils/dates.ts';
import { EmptyDataOverlay } from './EmptyDataOverlay.tsx';
import { useFormWidget } from './use-form-widget.ts';

const widgetTitle = 'Form Completion Time Trends';

const infoTemplate = (
  <InfoTemplate>
    <InfoSection title="What it is &amp; Problems it Solves">
      <InfoParagraph>
        Shows how long it takes patients to complete both&nbsp;
        <InfoEmphasis>required</InfoEmphasis>
        &nbsp;and&nbsp;
        <InfoEmphasis>non-required</InfoEmphasis>
        &nbsp;forms over time, broken down by form type. This helps identify delays, compare
        engagement patterns across form categories, and improve readiness workflows before
        appointments.
      </InfoParagraph>
    </InfoSection>

    <InfoSection title="How it Works (Calculation)" sx={{ mb: 2 }}>
      <InfoList
        component="ol"
        boxProps={{ sx: { pl: 5, mb: 2 } }}
        items={[
          <>
            <Typography variant="body2" fontWeight="bold">
              Filter forms to include only those that:
            </Typography>
            <InfoList
              items={[
                'Are part of the selected form set (may include both required and non-required forms depending on filters)',
                'Have both an assignment time and a completion time',
                'Were completed within the selected date range',
              ]}
            />
          </>,
          <>
            <Typography variant="body2" fontWeight="bold">
              Measure completion time for each form:
            </Typography>
            <InfoList
              items={[
                'Time is calculated in seconds from when the form was assigned to when it was completed, then converted into hours or days by the front end',
                'Find the median (middle value) of all completion times (not the average)',
              ]}
            />
          </>,
          <>
            <Typography variant="body2" fontWeight="bold">
              Display the Result:
            </Typography>
            <InfoList
              items={[
                'If the median is less than 1 day → show in hours (e.g., 3.7 hours)',
                'If the median is 1 day or more → show in days (e.g., 2.1 days)',
              ]}
            />
          </>,
        ]}
      />

      <InfoSection title="Includes" indented>
        <InfoList
          items={[
            'Forms with valid assigned and completed dates',
            'Forms completed within the selected date range (even if assigned earlier)',
            'Required and non-required forms (depending on applied filters)',
            'Deleted forms',
          ]}
        />
      </InfoSection>

      <InfoSection title="Excludes" indented>
        <InfoList items={['Forms never completed']} />
      </InfoSection>

      <InfoSection title="Notes &amp; Caveats" indented>
        <InfoList
          items={[
            'Time is always calculated in seconds and converted for display by the front end',
            'Metric uses the median to avoid skew from extreme delays; do not interpret as an average',
            'Completion date determines inclusion, not assignment date—forms may have been assigned earlier',
            'Deleted forms are included in calculations',
            'Visual trends may appear “flat” if completion times are very similar or tightly grouped',
            'Data updates nightly, so very recent changes may not yet appear.',
          ]}
        />
      </InfoSection>
    </InfoSection>
  </InfoTemplate>
);

/**
 * Post-processes the response from the form completion time trends
 * used to memoize to prevent executing the function on every render.
 */
function postProcessResponse(rawResponse: GetFormCompletionTimeTrendsResponse) {
  // Make any necessary data transformations on the response here
  const response = {
    ...rawResponse,
    data: rawResponse.data.map((item) => ({
      ...item,
      period: new Date(item.period),
    })),
  };

  // Need to calc the max median seconds to complete in days for later use
  const maxDays = Math.ceil(
    response.data.reduce((acc, cur) => {
      const { medianSecondsToComplete } = cur;
      if (acc < medianSecondsToComplete) {
        acc = medianSecondsToComplete;
      }
      return acc;
    }, 0) / DAY_IN_SECONDS
  );

  return { ...response, maxDays };
}

export function FormCompletionTimeTrends() {
  const widget = useFormWidget();

  const theme = useTheme();

  // Fetch form trends data
  const { data, isLoading } = useQuery({
    ...formCompletionTimeTrends(widget.getCommonRequestParams()),
    select: postProcessResponse,
  });

  if (isLoading) {
    return (
      <Base title={widgetTitle}>
        <Skeleton variant="rounded" height={WIDGET_CHART_HEIGHT} />
      </Base>
    );
  }

  return (
    <Base
      title={widgetTitle}
      infoTemplate={infoTemplate}
      drilldownRoutePath={widget.getDrilldownRoutePath()}
    >
      {(isOpenedInDialog) => (
        <Box height="100%">
          {data && data.data.length > 0 ? (
            <ChartDataProvider
              colors={(mode) => CHART_SERIES_COLORS[mode]}
              dataset={data.data}
              height={isOpenedInDialog ? undefined : WIDGET_CHART_HEIGHT}
              // Need to set right marin to fit x-axis tick label
              margin={{ right: 30 }}
              series={[
                {
                  dataKey: 'medianSecondsToComplete',
                  label: 'Median Completion Time',
                  labelMarkType: 'circle',
                  type: 'line',
                  valueFormatter: (value) => {
                    if (value === null) return '';
                    return formatDurationFromSeconds(value);
                  },
                },
              ]}
              xAxis={[
                {
                  dataKey: 'period',
                  height: 60,
                  label: 'Dates',
                  scaleType: 'time',
                  ...CHART_X_AXIS_CONFIG[data.groupedBy],
                },
              ]}
              yAxis={[
                {
                  label: 'Days',
                  // Set the tick intervals to the unique, whole days covered in the dataset
                  // The maxDays should organically be a positive integer as there should never
                  // be an instance where a forms completed date is older than its created
                  // date. The only way this tickInterval array could be empty is if the data
                  // returned from the server was an empty array and in that case we wouldn't
                  // even render the chart.
                  tickInterval: Array.from(
                    { length: data.maxDays + 1 },
                    (_, i) => i * DAY_IN_SECONDS
                  ),
                  min: 0,
                  tickMinStep: DAY_IN_SECONDS,
                  /** Format the y-axis tick labels */
                  valueFormatter: (value: number) => {
                    // Convert the value from seconds to days
                    return String(Math.floor(value / DAY_IN_SECONDS));
                  },
                },
              ]}
            >
              <ChartsLegend
                sx={{
                  justifyContent: 'center',
                  '.MuiChartsLegend-label': {
                    color: theme.palette.muted[4],
                  },
                }}
              />
              <ChartsSurface>
                <ChartsXAxis sx={CHART_AXIS_STYLES(theme)} />
                <ChartsYAxis sx={CHART_AXIS_STYLES(theme)} />
                <LinePlot />
                <MarkPlot />
                <ChartsAxisHighlight x="line" y="none" />
                <ChartsTooltip />
              </ChartsSurface>
            </ChartDataProvider>
          ) : (
            <EmptyDataOverlay variant={isOpenedInDialog ? 'dialog' : 'chart'} />
          )}
        </Box>
      )}
    </Base>
  );
}
